import React, {useEffect, useMemo, useRef, useState} from 'react';
import SearchBar from '../search/SearchBar';
import TuningPanel from './TuningPanel';
import {search} from '../../libraries/DnaApi';
import {FAMILY_LABELS, getLab, resetRecommendSettings, saveRecommendSettings} from '../../libraries/AdminApi';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';
const MAX_COLUMNS = 6;
const GROUPS = [
    {key: 'energy', color: '#f59e0b', label: 'Energy', hint: 'Where the power sits in the frequency range (the mix EQ).'},
    {key: 'timbre', color: '#a78bfa', label: 'Timbre', hint: 'Instrument color: the average sound texture.'},
    {key: 'timbre_var', color: '#f472b6', label: 'Timbre variation', hint: 'How much the texture changes (quiet verse, loud chorus).'},
    {key: 'harmony', color: '#22d3ee', label: 'Harmony', hint: 'Which of the 12 notes carry the energy: key center, chord color.'},
    {key: 'harmony_var', color: '#60a5fa', label: 'Harmony movement', hint: 'How much the harmony moves: vamps vs real progressions.'},
];

const pct = (v, d = 0) => (v == null ? '—' : (v * 100).toFixed(d) + '%');
const plus = (v) => (v ? '+' + (v * 100).toFixed(1) : '0');

const toParams = (v) => (v ? {
    genre_weight: v.genre_exact,
    family_weight: v.genre_family,
    era_weight: v.era,
    half_life: v.era_half_life,
    genre_lock: v.genre_lock ? 1 : 0,
    era_lock: v.era_lock ? 1 : 0,
    era_years: v.era_years,
} : {});

/** Original song's value vs the catalog: red above average, blue below. */
const zColor = (z) => {
    const a = 0.25 + Math.min(Math.abs(z) / 2.5, 1) * 0.75;
    return z >= 0 ? `rgba(250,45,72,${a})` : `rgba(96,165,250,${a})`;
};

// A value matches within half a standard deviation, is close within one.
const MATCH = 0.5;
const CLOSE = 1.0;
const GREEN = '#34d399';
const YELLOW = '#fbbf24';
const RED = '#fa2d48';
const gapColor = (gap) => (gap < MATCH ? GREEN : gap < CLOSE ? YELLOW : RED);
/**
 * A category's cost vs its fair share (its value count / 84): under → green,
 * about fair → yellow, well over → red.
 */
const costColor = (ratio) => (ratio < 0.8 ? GREEN : ratio < 1.25 ? YELLOW : RED);
const pts = (v) => (v * 100).toFixed(1);

function Cover({song, size = 56}) {
    return <img src={song?.cover || FALLBACK_COVER} alt="" style={{width: size, height: size}}
                className="flex-none rounded-lg object-cover shadow-[0_10px_24px_rgba(0,0,0,0.4)]"/>;
}

function PlayButton({song, audio}) {
    const on = audio.playing === song?.id;
    if (!song?.song_url) {
        return null;
    }
    return (
        <button type="button" onClick={() => audio.toggle(song.id, song.song_url)} title={on ? 'Pause' : 'Play the preview'}
                className={'flex h-7 w-7 flex-none items-center justify-center rounded-full transition '
                    + (on ? 'bg-apple-red text-white' : 'bg-white/10 text-white/80 hover:bg-white/20')}>
            <i className={'fa-solid text-[10px] ' + (on ? 'fa-pause' : 'fa-play pl-0.5')}/>
        </button>
    );
}

/**
 * How the score is built, exactly: 100 minus what each category costs gives
 * the raw similarity; then the crowding correction and the genre and era
 * bonuses land on the displayed score.
 */
function Waterfall({c, stats}) {
    const raw = c.similarity;
    const steps = [
        {label: 'crowding', value: c.score_parts.sound - raw, color: '#94a3b8'},
        {label: 'genre', value: c.score_parts.genre, color: GREEN},
        {label: 'era', value: c.score_parts.era, color: '#fbbf24'},
    ];
    const seg = (v) => ({width: `${Math.max(Math.abs(v) * 100, 0)}%`});
    let cursor = raw;
    return (
        <div className="mt-4 border-t border-white/[0.06] pt-3">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-white/40">How the score is built</div>
            {/* 100 points, minus what each category costs */}
            <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-white/[0.06]">
                <span className="h-full bg-white/30" style={seg(raw)} title={`Raw similarity ${pts(raw)}`}/>
                {GROUPS.map((g) => (
                    <span key={g.key} className="h-full" style={{...seg(stats[g.key].lost), background: g.color}}
                          title={`${g.label} costs ${pts(stats[g.key].lost)} pts`}/>
                ))}
            </div>
            {/* raw similarity, then the adjustments */}
            <div className="relative mt-1.5 h-2.5 rounded-full bg-white/[0.06]">
                <span className="absolute inset-y-0 left-0 rounded-l-full bg-white/30" style={seg(raw)}/>
                {steps.map((st) => {
                    const left = st.value >= 0 ? cursor : cursor + st.value;
                    cursor += st.value;
                    return (
                        <span key={st.label} className="absolute inset-y-0" title={`${st.label} ${st.value >= 0 ? '+' : ''}${pts(st.value)}`}
                              style={{left: `${left * 100}%`, ...seg(st.value), background: st.value >= 0 ? st.color : RED}}/>
                    );
                })}
            </div>
            <p className="mt-2 text-[10.5px] leading-relaxed text-white/50">
                100 − {GROUPS.map((g) => pts(stats[g.key].lost)).join(' − ')} = <b className="text-white/80">{pts(raw)}</b> raw
                {steps.map((st) => (
                    <span key={st.label}> {st.value >= 0 ? '+' : '−'} {pts(Math.abs(st.value))} {st.label}</span>
                ))}
                {' '}= <b className="text-[#ff97a5]">{pct(c.score)}</b>
            </p>
        </div>
    );
}

/** Frosted search modal used to swap a comparison column. */
function SwapModal({song, onPick, onClose}) {
    const ref = useRef(null);
    useEffect(() => {
        setTimeout(() => ref.current?.focus(), 30);
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[14vh] backdrop-blur-sm" onMouseDown={onClose}>
            <div className="w-full max-w-[620px] rounded-3xl bg-[#1c140f]/90 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_40px_100px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
                 onMouseDown={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">Swap</div>
                        <div className="mt-0.5 text-[15px] font-bold">Replace {song.title} with…</div>
                    </div>
                    <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white/80 hover:bg-white/20">
                        <i className="fa-solid fa-xmark"/>
                    </button>
                </div>
                <SearchBar hero ref={ref} onPlay={onPick} onResults={() => {}}/>
            </div>
        </div>
    );
}

/**
 * The Song Lab: a seed song, its recommendation list under live scoring
 * options, and a side-by-side breakdown against up to five candidates.
 */
export default function LabPage({songId, ids, onOpen, onColumns, onFollow, audio}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tuning, setTuning] = useState(null);
    const [saved, setSaved] = useState(null);
    const [busy, setBusy] = useState(false);
    // Which profile the panel edits: the Default, or the song's genre profile.
    const [target, setTarget] = useState('default');
    const [replacing, setReplacing] = useState(null);
    const [openGroups, setOpenGroups] = useState({});
    const seq = useRef(0);
    const timer = useRef(null);

    // No ids = follow the live top 5; any edit pins the columns.
    const idsKey = (ids || []).join(',');
    const following = !idsKey;

    // Reset tuning to the saved defaults when the seed changes.
    useEffect(() => { setTuning(null); setData(null); }, [songId]);

    useEffect(() => {
        if (!songId) {
            return undefined;
        }
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            const n = ++seq.current;
            setLoading(true);
            getLab(songId, {...toParams(tuning), ids: idsKey, limit: 25})
                .then((d) => {
                    if (n !== seq.current) return;
                    setData(d);
                    setError(null);
                    if (!tuning) {
                        const t = d.profile ? 'profile' : 'default';
                        const v = t === 'profile' ? d.profiles.current : d.profiles.default;
                        setTarget(t); setTuning(v); setSaved(v);
                    }
                })
                .catch((e) => n === seq.current && setError(e.message))
                .finally(() => n === seq.current && setLoading(false));
        }, tuning ? 300 : 0);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [songId, idsKey, JSON.stringify(tuning)]);

    const openFromSearch = (q) => search(encodeURIComponent(q)).then((r) => r?.hits?.[0] && onOpen(r.hits[0].id));

    const listRank = useMemo(() => {
        const m = {};
        (data?.list || []).forEach((s, i) => { m[s.id] = i + 1; });
        return m;
    }, [data]);

    const groupStats = useMemo(() => {
        if (!data) return {};
        const out = {};
        const seedE = data.seed.embedding || [];
        data.candidates.forEach((c) => {
            const total = seedE.reduce((acc, v, i) => acc + (v - c.embedding[i]) ** 2, 0) || 1;
            out[c.song.id] = Object.fromEntries(GROUPS.map((g) => {
                const idx = data.dimensions.filter((d) => d.group === g.key).map((d) => d.i);
                const d2 = idx.reduce((acc, i) => acc + (seedE[i] - c.embedding[i]) ** 2, 0);
                const share = d2 / total;
                return [g.key, {
                    lost: d2 / 2,
                    share,
                    ratio: share / (idx.length / 84),
                }];
            }));
        });
        return out;
    }, [data]);

    const current = following ? (data?.candidates || []).map((c) => c.song.id) : (ids || []);
    const setColumn = (index, id) => {
        const next = [...current];
        if (index == null) {
            if (next.includes(id)) return;
            if (next.length >= MAX_COLUMNS - 1) next.pop();
            next.push(id);
        } else {
            next[index] = id;
        }
        onColumns(next);
    };
    const removeColumn = (index) => onColumns(current.filter((_, i) => i !== index));

    const profileLabel = data?.profile ? (data.profiles.label || FAMILY_LABELS[data.profile] || data.profile) : null;
    const hasOverrides = data && Object.keys(data.profiles.current_overrides || {}).length > 0;
    const switchTarget = (t) => {
        const v = t === 'profile' ? data.profiles.current : data.profiles.default;
        setTarget(t); setTuning(v); setSaved(v);
    };
    const save = () => {
        setBusy(true);
        saveRecommendSettings(target === 'profile' ? data.profile : 'default', tuning)
            .then(() => {
                setSaved(tuning);
                setData((d) => ({...d, profiles: target === 'profile'
                    ? {...d.profiles, current: tuning, current_overrides: tuning}
                    : {...d.profiles, default: tuning}}));
            })
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
    };
    const removeProfile = () => {
        if (!window.confirm(`Remove the ${profileLabel} profile? ${profileLabel} songs go back to the Default values.`)) return;
        setBusy(true);
        resetRecommendSettings(data.profile)
            .then((r) => {
                setData((d) => ({...d, profiles: {...d.profiles, current: r.default, current_overrides: {}}}));
                setTarget('profile'); setTuning(r.default); setSaved(r.default);
            })
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
    };

    const columns = data ? [{seed: true, ...data.seed}, ...data.candidates] : [];
    const gridStyle = {gridTemplateColumns: `128px repeat(${columns.length}, minmax(150px, 1fr))`};

    const Row = ({label, hint, children, className = ''}) => (
        <>
            <div className="border-t border-white/[0.06] py-2 pr-3 text-[11.5px] font-semibold text-white/50" title={hint}>{label}</div>
            {React.Children.map(children, (child) => (
                <div className={'border-t border-white/[0.06] px-2 py-2 text-[12px] ' + className}>{child}</div>
            ))}
        </>
    );

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-[28px] font-bold tracking-[-0.015em]">Song Lab</h1>
                    <p className="mt-1 text-[13px] text-white/45">See why the engine matches songs, and tune how it scores them.</p>
                </div>
                <SearchBar onPlay={(h) => onOpen(h.id)} onResults={openFromSearch}/>
            </div>

            {!songId && (
                <div className="mt-24 text-center text-white/40">
                    <i className="fa-solid fa-flask text-3xl text-apple-red/70"/>
                    <p className="mt-4 text-[14px]">Search for a song to open it in the lab.</p>
                </div>
            )}

            {error && <p className="mt-6 text-[13px] text-apple-red">{error}</p>}
            {songId && !data && !error && <p className="mt-10 text-[13px] text-white/40">Loading the lab…</p>}

            {data && (
                <div className="mt-8 flex flex-col gap-6 xl:flex-row">
                    <div className="min-w-0 flex-1">
                        {/* Seed */}
                        <div className="flex items-center gap-5">
                            <Cover song={data.seed.song} size={112}/>
                            <div className="min-w-0">
                                <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-apple-red">Original</div>
                                <div className="mt-1 flex items-center gap-3">
                                    <h2 className="truncate font-heading text-[26px] font-bold tracking-[-0.015em]">{data.seed.song.title}</h2>
                                    <PlayButton song={data.seed.song} audio={audio}/>
                                </div>
                                <div className="text-[14px] text-white/60">{data.seed.song.artist}</div>
                                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                                    {[data.seed.song.album, data.seed.song.year, data.seed.effective_genre,
                                        data.seed.family && 'family: ' + data.seed.family,
                                        data.space === 'full' ? 'full-song fingerprint' : 'preview fingerprint',
                                        'stats v' + data.stats_version].filter(Boolean).map((t) => (
                                        <span key={t} className="rounded-md bg-white/[0.07] px-2 py-0.5 text-white/60">{t}</span>
                                    ))}
                                </div>
                            </div>
                            {loading && <i className="fa-solid fa-circle-notch fa-spin ml-auto text-white/30"/>}
                        </div>

                        {/* Side by side */}
                        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-white/50">
                            <span className="flex items-center gap-4">
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{background: GREEN}}/>match</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{background: YELLOW}}/>close</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{background: RED}}/>different</span>
                                <span className="text-white/35">original: <span style={{color: RED}}>above</span> / <span style={{color: '#60a5fa'}}>below</span> catalog average</span>
                            </span>
                            {following ? (
                                <span className="rounded-full bg-white/[0.07] px-3 py-1">Following the live top 5</span>
                            ) : (
                                <button type="button" onClick={onFollow} className="rounded-full bg-white/[0.07] px-3 py-1 font-semibold text-white/70 hover:text-white">
                                    <i className="fa-solid fa-thumbtack mr-1.5 text-[10px]"/>Pinned · follow the top 5
                                </button>
                            )}
                        </div>
                        <div className="mt-3 overflow-x-auto rounded-2xl bg-white/[0.04] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                            <div className="grid min-w-max items-center" style={gridStyle}>
                                <div/>
                                {columns.map((c, ci) => (
                                    <div key={c.song.id + '-' + ci} className="px-2 pb-3">
                                        <div className="flex items-start gap-2.5">
                                            <Cover song={c.song} size={48}/>
                                            <div className="min-w-0 flex-1">
                                                <button type="button" onClick={() => !c.seed && onOpen(c.song.id)}
                                                        title={c.seed ? '' : 'Open this song as the original'}
                                                        className="block w-full truncate text-left text-[12.5px] font-bold hover:underline">
                                                    {c.song.title}
                                                </button>
                                                <div className="truncate text-[11px] text-white/50">{c.song.artist}</div>
                                                <div className="mt-1.5 flex items-center gap-1.5">
                                                    <PlayButton song={c.song} audio={audio}/>
                                                    {!c.seed && (
                                                        <>
                                                            <button type="button" onClick={() => setReplacing(ci - 1)} title="Replace with another song"
                                                                    className="h-7 rounded-full bg-white/10 px-2.5 text-[10.5px] font-semibold text-white/70 hover:bg-white/20">
                                                                Swap
                                                            </button>
                                                            <button type="button" onClick={() => removeColumn(ci - 1)} title="Remove column"
                                                                    className="h-7 w-7 rounded-full text-white/40 hover:text-white">
                                                                <i className="fa-solid fa-xmark text-[11px]"/>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <Row label="Score" hint="What the app shows: sound + genre + era, capped at 99%.">
                                    {columns.map((c) => (c.seed
                                        ? <span className="text-white/30">—</span>
                                        : (
                                            <span className="flex items-baseline gap-2">
                                                <span className="text-[22px] font-extrabold tabular-nums text-[#ff97a5]">{pct(c.score)}</span>
                                                {Math.round(c.score * 100) !== Math.round(c.score_default * 100) && (
                                                    <span className="text-[11px] text-white/45">was {pct(c.score_default)}</span>
                                                )}
                                                {c.locked_out && <span className="rounded bg-amber-400/[0.15] px-1.5 text-[9.5px] font-bold uppercase text-amber-300">locked out</span>}
                                            </span>
                                        )))}
                                </Row>
                                <Row label="List position" hint="Where it lands in the 25-song recommendation list with these settings.">
                                    {columns.map((c) => (c.seed ? '—' : (listRank[c.song.id] ? `#${listRank[c.song.id]}` : <span className="text-white/40">not in top 25</span>)))}
                                </Row>
                                <Row label="Sound rank" hint="How many songs in the whole catalog sound closer to the original (raw fingerprint distance only).">
                                    {columns.map((c) => (c.seed ? '—' : `#${(c.sound_rank || 0).toLocaleString()}`))}
                                </Row>
                                <Row label="Sound" hint="Similarity after the hub correction: the base of the score.">
                                    {columns.map((c) => (c.seed ? '—' : pct(c.score_parts.sound, 1)))}
                                </Row>
                                <Row label="Genre bonus" hint="Share of the gap to 100% closed for the same genre or family.">
                                    {columns.map((c) => (c.seed ? '—' : <span>{plus(c.score_parts.genre)} <span className="text-white/40">({c.genre_match})</span></span>))}
                                </Row>
                                <Row label="Era bonus" hint="Share of the gap closed for a close release year.">
                                    {columns.map((c) => (c.seed ? '—' : <span>{plus(c.score_parts.era)} <span className="text-white/40">{c.year_gap != null ? `(${c.year_gap} yrs apart)` : ''}</span></span>))}
                                </Row>
                                <Row label="Raw similarity" hint="Cosine similarity of the two fingerprints, before any correction.">
                                    {columns.map((c) => (c.seed ? '—' : pct(c.similarity, 1)))}
                                </Row>
                                <Row label="Hub factor" hint="Below 1: the candidate sits in a sparse area and is treated as closer. Above 1: a crowded area, treated as farther.">
                                    {columns.map((c) => (c.seed ? <span className="text-white/40">k20 {c.k20}</span> : `×${c.hub_factor.toFixed(3)}`))}
                                </Row>
                                <Row label="Mutual" hint="Both songs are among each other's closest neighbors (5% edge).">
                                    {columns.map((c) => (c.seed ? '—' : (c.is_mutual ? <span className="text-emerald-300">yes</span> : <span className="text-white/40">no</span>)))}
                                </Row>

                                <Row label="Genre" className="text-white/70">{columns.map((c) => c.effective_genre || c.song.genre || '—')}</Row>
                                <Row label="Year" className="text-white/70">{columns.map((c) => c.song.year || '—')}</Row>
                                <Row label="BPM (est.)" className="text-white/70">{columns.map((c) => c.song.bpm || '—')}</Row>
                                <Row label="Key (est.)" className="text-white/70">{columns.map((c) => c.song.song_key || '—')}</Row>
                                <Row label="ISRC" className="font-mono text-[11px] text-white/60">{columns.map((c) => c.song.isrc || '—')}</Row>

                            </div>
                        </div>

                        {/* Sonic fingerprint cards */}
                        <div className="mt-6">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">Sonic fingerprints</div>
                                <div className="text-[11px] text-white/35">One dot per value. Hover a dot for the numbers.</div>
                            </div>
                            <div className="mt-3 grid gap-4" style={{gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'}}>
                                {columns.map((c, ci) => (
                                    <div key={c.song.id + '-card-' + ci}
                                         className={'rounded-2xl p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] '
                                             + (c.seed ? 'bg-white/[0.08] ring-1 ring-apple-red/40' : 'bg-white/[0.04]')}>
                                        <div className="flex items-center gap-3">
                                            <Cover song={c.song} size={40}/>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-[12.5px] font-bold">{c.song.title}</div>
                                                <div className="truncate text-[11px] text-white/50">{c.song.artist}</div>
                                            </div>
                                            {c.seed
                                                ? <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-apple-red">Original</span>
                                                : <span className="text-[18px] font-extrabold tabular-nums text-[#ff97a5]">{pct(c.score)}</span>}
                                        </div>
                                        <div className="mt-3 space-y-2.5">
                                            {GROUPS.map((g) => {
                                                const dims = data.dimensions.filter((d) => d.group === g.key);
                                                const st = c.seed ? null : groupStats[c.song.id]?.[g.key];
                                                return (
                                                    <div key={g.key} title={g.hint}>
                                                        <div className="flex items-baseline justify-between text-[10.5px]">
                                                            <span className="flex items-center gap-1.5 font-semibold text-white/55">
                                                                <span className="h-2 w-2 rounded-sm" style={{background: g.color}}/>{g.label}
                                                            </span>
                                                            {st && (
                                                                <span title={`Costs ${pct(st.share)} of the total difference; its fair share is ${pct(dims.length / 84)} (${dims.length} of 84 values).`}>
                                                                    <span className="font-bold tabular-nums" style={{color: costColor(st.ratio)}}>−{pts(st.lost)} pts</span>
                                                                    <span className="ml-1.5 text-white/35">{pct(st.share)} of gap</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-1 grid gap-[4px]" style={{gridTemplateColumns: 'repeat(20, minmax(0, 1fr))'}}>
                                                            {dims.map((d) => {
                                                                const z = c.z[d.i];
                                                                const gap = Math.abs(z - data.seed.z[d.i]);
                                                                return (
                                                                    <span key={d.i}
                                                                          title={`${d.label}: ${z.toFixed(2)} sd${c.seed ? '' : ` (original ${data.seed.z[d.i].toFixed(2)}, gap ${gap.toFixed(2)})`}`}
                                                                          className="aspect-square w-full rounded-full"
                                                                          style={{background: c.seed ? zColor(z) : gapColor(gap)}}/>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {!c.seed && groupStats[c.song.id] && <Waterfall c={c} stats={groupStats[c.song.id]}/>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Every variable */}
                        <div className="mt-6 rounded-2xl bg-white/[0.04] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                            <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">All 84 fingerprint values</div>
                            <p className="mt-1 text-[11.5px] text-white/40">z = how far from the catalog average, in standard deviations (±2 is unusual). Hover for raw values.</p>
                            {GROUPS.map((g) => {
                                const dims = data.dimensions.filter((d) => d.group === g.key);
                                const open = openGroups[g.key];
                                return (
                                    <div key={g.key} className="mt-3">
                                        <button type="button" onClick={() => setOpenGroups({...openGroups, [g.key]: !open})}
                                                className="flex w-full items-center gap-2 text-left text-[12.5px] font-bold">
                                            <i className={'fa-solid fa-chevron-right text-[9px] text-white/40 transition ' + (open ? 'rotate-90' : '')}/>
                                            {g.label} <span className="font-normal text-white/40">({dims.length} values) {g.hint}</span>
                                        </button>
                                        {open && (
                                            <div className="mt-2 overflow-x-auto">
                                                <table className="text-[11.5px] tabular-nums">
                                                    <thead>
                                                        <tr className="text-white/40">
                                                            <th className="py-1 pr-4 text-left font-semibold">Value</th>
                                                            {columns.map((c, ci) => <th key={ci} className="max-w-[130px] truncate px-2 py-1 text-right font-semibold">{c.song.title}</th>)}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dims.map((d) => (
                                                            <tr key={d.i} className="border-t border-white/[0.05]">
                                                                <td className="py-1 pr-4 text-white/60">{d.label}</td>
                                                                {columns.map((c, ci) => (
                                                                    <td key={ci} className="px-2 py-1 text-right" title={`raw ${c.raw[d.i]}`}>
                                                                        <span className="rounded px-1.5 py-0.5"
                                                                              style={{background: c.seed ? zColor(c.z[d.i]).replace(/[0-9.]+\)$/, '0.45)') : gapColor(Math.abs(c.z[d.i] - data.seed.z[d.i])) + '55'}}>
                                                                            {c.z[d.i].toFixed(2)}
                                                                        </span>
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {replacing != null && columns[replacing + 1] && (
                        <SwapModal song={columns[replacing + 1].song}
                                   onPick={(h) => { setColumn(replacing, h.id); setReplacing(null); }}
                                   onClose={() => setReplacing(null)}/>
                    )}

                    {/* Rail: scoring + the list */}
                    <div className="w-full flex-none space-y-4 xl:w-[300px]">
                        <TuningPanel values={tuning} saved={saved} onChange={setTuning} busy={busy}
                                     onSave={save} onRevert={() => setTuning(saved)}
                                     saveLabel={target === 'profile' ? `Save ${profileLabel} profile` : 'Save as default'}
                                     header={data.profile && (
                                         <div className="mt-3">
                                             <div className="flex rounded-xl bg-white/[0.06] p-1 text-[11.5px] font-semibold">
                                                 {[['profile', profileLabel], ['default', 'Default']].map(([t, label]) => (
                                                     <button key={t} type="button" onClick={() => switchTarget(t)}
                                                             className={'flex-1 rounded-lg py-1.5 transition ' + (target === t ? 'bg-white/[0.14] text-white' : 'text-white/50 hover:text-white')}>
                                                         {label}
                                                     </button>
                                                 ))}
                                             </div>
                                             <p className="mt-2 text-[10.5px] leading-relaxed text-white/40">
                                                 {target === 'profile'
                                                     ? (hasOverrides
                                                         ? `${profileLabel} songs use their own values.`
                                                         : `${profileLabel} songs use the Default values. Saving here creates a ${profileLabel} profile.`)
                                                     : (hasOverrides
                                                         ? `Editing Default. This song uses the ${profileLabel} profile, so the preview shows what Default would do.`
                                                         : 'Editing Default: every genre without its own profile.')}
                                             </p>
                                         </div>
                                     )}
                                     footer={target === 'profile' && hasOverrides && (
                                         <button type="button" onClick={removeProfile} disabled={busy}
                                                 className="mt-2 w-full rounded-xl py-1.5 text-[11.5px] text-white/45 transition hover:text-apple-red">
                                             Remove {profileLabel} profile
                                         </button>
                                     )}/>
                        <div className="rounded-2xl bg-white/[0.06] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
                            <div className="px-1 pb-2 text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/45">
                                Recommendations ({data.list.length})
                            </div>
                            {data.list.map((s, i) => {
                                const inGrid = current.includes(s.id);
                                return (
                                    <div key={s.id} className="flex items-center gap-2.5 rounded-lg p-1.5 hover:bg-white/[0.05]">
                                        <span className="w-5 text-right text-[10.5px] tabular-nums text-white/35">{i + 1}</span>
                                        <Cover song={s} size={32}/>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[12px] font-semibold">{s.title}</span>
                                            <span className="block truncate text-[10.5px] text-white/45">{s.artist}{s.year ? ' · ' + s.year : ''}</span>
                                        </span>
                                        <span className="text-[11px] font-bold tabular-nums text-white/70">{pct(s.proximity)}</span>
                                        <button type="button" onClick={() => setColumn(null, s.id)} disabled={inGrid}
                                                title={inGrid ? 'In the comparison' : 'Add to the comparison'}
                                                className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white/10 text-[10px] text-white/70 hover:bg-white/20 disabled:opacity-30">
                                            <i className={'fa-solid ' + (inGrid ? 'fa-check' : 'fa-plus')}/>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
